import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Calculator, FilePlus2, Presentation, Search } from "lucide-react";
import { offerApi } from "../../services/offerApi";
import NewDocumentDialog from "./NewDocumentDialog";
import { formatEuro } from "../../utils/offerCalc";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const TABS = [
  { key: "chiffrages", label: "Chiffrages" },
  { key: "propositions", label: "Propositions" },
];

const OffersPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("onglet") === "propositions" ? "propositions" : "chiffrages";
  const proposals = tab === "propositions";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState("Chargement…");

  const load = useCallback(async () => {
    try {
      const query = { q: search, include_archived: archived, limit: 200 };
      const data = await (proposals ? offerApi.proposals(query) : offerApi.list(query));
      setItems(data.items || []);
      setStatus("");
    } catch (err) {
      setStatus(err.message);
    }
  }, [search, archived, proposals]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    offerApi.permissions().then((data) => setCanEdit(Boolean(data.can_edit))).catch(() => setCanEdit(false));
  }, []);

  return (
    <div className="mx-auto max-w-7xl space-y-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <Calculator className="h-3.5 w-3.5" />
            Offres
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Chiffrages et propositions</h1>
          <p className="mt-1 text-sm text-slate-500">
            {canEdit ? "Vous pouvez créer et modifier les offres." : "Lecture seule : le droit « Offres » permet de modifier."}
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            {proposals ? <Presentation className="mr-2 h-4 w-4" /> : <FilePlus2 className="mr-2 h-4 w-4" />}
            {proposals ? "Nouvelle proposition" : "Nouveau chiffrage"}
          </button>
        )}
      </div>

      <div role="tablist" aria-label="Type d'offre" className="flex gap-1 border-b border-slate-200">
        {TABS.map((item) => (
          <button key={item.key} type="button" role="tab" aria-selected={tab === item.key}
            onClick={() => { setItems([]); setStatus("Chargement…"); setParams(item.key === "chiffrages" ? {} : { onglet: item.key }); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === item.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher : titre, affaire, projet, client"
            aria-label={proposals ? "Rechercher une proposition" : "Rechercher un chiffrage"}
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={archived} onChange={(event) => setArchived(event.target.checked)} />
          Afficher les archivés
        </label>
      </div>

      {proposals ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Affaire</th>
                <th className="px-4 py-3">Proposition</th>
                <th className="px-4 py-3">Client · projet</th>
                <th className="px-4 py-3">Chiffrage repris</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Mis à jour</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id} onClick={() => navigate(`/tools/offres/propositions/${row.id}`)}
                  className={`cursor-pointer hover:bg-blue-50/40 ${row.archived ? "text-slate-400" : "text-slate-800"}`}>
                  <td className="whitespace-nowrap px-4 py-3 font-medium">{row.affaire_number || "—"}</td>
                  <td className="px-4 py-3">{row.title}</td>
                  <td className="px-4 py-3 text-slate-500">{[row.client_name, row.project_name].filter(Boolean).join(" · ")}</td>
                  <td className="px-4 py-3 text-slate-500">{row.estimate_title || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                    v{row.revision_number}{row.revision_label ? ` · ${row.revision_label}` : ""}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                    {formatDate(row.updated_at)}{row.last_author ? ` · ${row.last_author}` : ""}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">{status || "Aucune proposition pour l'instant."}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Affaire</th>
              <th className="px-4 py-3">Chiffrage</th>
              <th className="px-4 py-3">Client · projet</th>
              <th className="px-4 py-3 text-right">Prix HT</th>
              <th className="px-4 py-3 text-right">Prix remisé</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3">Mis à jour</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((row) => (
              <tr
                key={row.id}
                onClick={() => navigate(`/tools/offres/${row.id}`)}
                className={`cursor-pointer hover:bg-blue-50/40 ${row.archived ? "text-slate-400" : "text-slate-800"}`}
              >
                <td className="whitespace-nowrap px-4 py-3 font-medium">{row.affaire_number || "—"}</td>
                <td className="px-4 py-3">{row.title}</td>
                <td className="px-4 py-3 text-slate-500">{[row.client_name, row.project_name].filter(Boolean).join(" · ")}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-slate-500">{formatEuro(row.total_gross)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-blue-700">{formatEuro(row.total_net)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                  v{row.revision_number}{row.revision_label ? ` · ${row.revision_label}` : ""}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                  {formatDate(row.updated_at)}{row.last_author ? ` · ${row.last_author}` : ""}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">{status || "Aucun chiffrage pour l'instant."}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {creating && (
        <NewDocumentDialog kind={proposals ? "proposal" : "estimate"} onClose={() => setCreating(false)}
          onCreated={(path) => navigate(path)} />
      )}
    </div>
  );
};

export default OffersPage;
